import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Section from '../common/Section';
import ScrollIndicator from '../ui/ScrollIndicator';
import TypeWriter from '../ui/TypeWriter';
import { HERO_CONTENT } from '../../utils/constants';

const Hero: React.FC = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [showSubtext, setShowSubtext] = useState(false);
    const [hasTyped, setHasTyped] = useState(false);

    useEffect(() => {
        if (!hasTyped) {
            setIsLoaded(true);
            setHasTyped(true);
        }
    }, [hasTyped]);

    return (
        <Section id="home" fullHeight noPadding className="relative overflow-hidden">
            {/* Modern Liquid Glass Background */}
            <div className="absolute inset-0 liquid-glass-hero">
                {/* Primary Dynamic Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-sky-50/80 via-white/90 to-purple-50/70 animate-liquid-flow" />

                {/* Liquid Glass Floating Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    {/* Primary Liquid Orb */}
                    <motion.div
                        className="absolute top-20 right-20 w-80 h-80 liquid-orb-primary"
                        animate={{
                            scale: [1, 1.15, 0.95, 1.1, 1],
                            rotate: [0, 120, 240, 360],
                            x: [0, 30, -20, 15, 0],
                            y: [0, -15, 25, -10, 0],
                        }}
                        transition={{
                            duration: 25,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    />

                    {/* Secondary Glass Bubble */}
                    <motion.div
                        className="absolute bottom-32 left-16 w-96 h-96 liquid-orb-secondary"
                        animate={{
                            scale: [1, 0.9, 1.2, 0.95, 1],
                            rotate: [360, 240, 120, 0],
                            x: [0, -25, 35, -10, 0],
                            y: [0, 20, -30, 15, 0],
                        }}
                        transition={{
                            duration: 30,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    />

                    {/* Floating Glass Fragments */}
                    <motion.div
                        className="absolute top-1/3 left-1/3 w-24 h-32 liquid-fragment"
                        animate={{
                            y: [-20, 30, -10, 25, -20],
                            x: [-15, 20, -5, 18, -15],
                            rotate: [0, 45, -30, 60, 0],
                            scale: [1, 1.1, 0.9, 1.05, 1],
                        }}
                        transition={{
                            duration: 18,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    />
                    <motion.div
                        className="absolute top-2/3 right-1/4 w-32 h-24 liquid-fragment-alt"
                        animate={{
                            y: [25, -15, 20, -25, 25],
                            x: [10, -30, 5, -20, 10],
                            rotate: [0, -60, 30, -45, 0],
                            scale: [1, 0.95, 1.15, 0.9, 1],
                        }}
                        transition={{
                            duration: 22,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    />
                </div>

                {/* Liquid Glass Refraction Grid */}
                <div className="absolute inset-0 opacity-[0.015]">
                    <div className="absolute inset-0 liquid-refraction-grid" />
                </div>

                {/* Dynamic Light Rays */}
                <div className="absolute inset-0">
                    <motion.div
                        className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-sky-300/20 to-transparent"
                        animate={{
                            opacity: [0.1, 0.4, 0.1],
                            scaleY: [0.8, 1.2, 0.8],
                        }}
                        transition={{
                            duration: 8,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    />
                    <motion.div
                        className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-purple-300/15 to-transparent"
                        animate={{
                            opacity: [0.1, 0.3, 0.1],
                            scaleY: [1.2, 0.8, 1.2],
                        }}
                        transition={{
                            duration: 12,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: 2,
                        }}
                    />
                </div>

                {/* Liquid Glass Surface Overlay */}
                <div className="absolute inset-0 liquid-glass-surface" />
            </div>

            {/* Content */}
            <div className="relative z-10 h-screen flex items-center">
                <div className="container mx-auto px-4 md:px-6 lg:px-8">
                    <AnimatePresence>
                        {isLoaded && (
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
                                className="max-w-4xl space-y-8"
                            >
                                {/* Main Content */}
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.2, duration: 0.8 }}
                                    className="relative"
                                >
                                    {/* Subtle Background Glow */}
                                    <div className="absolute inset-0 -m-8 bg-gradient-to-br from-white/20 via-white/10 to-white/20 rounded-[3rem] backdrop-blur-sm filter blur-xl" />

                                    <div className="relative space-y-6">
                                        {/* Japanese name with animation */}
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.4, duration: 0.8 }}
                                            className="space-y-3"
                                        >
                                            <div className="relative">
                                                {/* Text Glow Effect */}
                                                <div className="absolute inset-0 h1-display text-slate-900 jp-heading opacity-20 blur-lg">
                                                    <TypeWriter
                                                        text={HERO_CONTENT.nameJp}
                                                        delay={500}
                                                        speed={80}
                                                        className="text-gradient-purple"
                                                    />
                                                </div>
                                                <h1 className="relative h1-display text-slate-900 jp-heading">
                                                    <TypeWriter
                                                        text={HERO_CONTENT.nameJp}
                                                        delay={500}
                                                        speed={80}
                                                        className="text-gradient-purple"
                                                        onComplete={() => setShowSubtext(true)}
                                                    />
                                                </h1>
                                            </div>

                                            {/* English name */}
                                            <AnimatePresence>
                                                {showSubtext && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0 }}
                                                        transition={{ duration: 0.6 }}
                                                        className="relative"
                                                    >
                                                        {/* Subtle text shadow */}
                                                        <div className="absolute inset-0 text-2xl md:text-3xl text-slate-600/30 font-light tracking-wide blur-sm">
                                                            {HERO_CONTENT.name}
                                                        </div>
                                                        <p className="relative text-2xl md:text-3xl text-slate-700 font-light tracking-wide">
                                                            {HERO_CONTENT.name}
                                                        </p>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </motion.div>

                                        {/* Animated tagline */}
                                        <AnimatePresence>
                                            {showSubtext && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.3, duration: 0.6 }}
                                                    className="space-y-4"
                                                >
                                                    <div className="relative">
                                                        <div className="absolute inset-0 text-lg md:text-xl text-slate-600/30 font-medium blur-sm">
                                                            Content Planner
                                                        </div>
                                                        <p className="relative text-lg md:text-xl text-slate-800 leading-relaxed font-medium">
                                                            Content Planner
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <motion.div
                                                            className="h-px w-16 bg-gradient-to-r from-transparent to-sky-400"
                                                            initial={{ width: 0 }}
                                                            animate={{ width: 64 }}
                                                            transition={{ delay: 0.6, duration: 0.8 }}
                                                        />
                                                        <motion.div
                                                            initial={{ opacity: 0, scale: 0 }}
                                                            animate={{ opacity: 1, scale: 1 }}
                                                            transition={{ delay: 0.8, duration: 0.4 }}
                                                            className="w-3 h-3 rounded-full bg-gradient-to-r from-sky-400 to-cyan-400 animate-pulse shadow-lg shadow-sky-400/50"
                                                        />
                                                        <motion.div
                                                            className="h-px flex-1 bg-gradient-to-r from-sky-400 to-transparent"
                                                            initial={{ width: 0 }}
                                                            animate={{ width: '100%' }}
                                                            transition={{ delay: 1, duration: 0.8 }}
                                                        />
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        {/* CTA Buttons */}
                                        <AnimatePresence>
                                            {showSubtext && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.6, duration: 0.6 }}
                                                    className="flex flex-col sm:flex-row gap-4 pt-8"
                                                >
                                                    <motion.a
                                                        href="#works"
                                                        whileHover={{ scale: 1.05, y: -2 }}
                                                        whileTap={{ scale: 0.95 }}
                                                        className="btn-primary group relative overflow-hidden shadow-xl hover:shadow-sky-500/30"
                                                    >
                                                        <span className="relative z-10 font-semibold">View My Work</span>
                                                        <motion.div
                                                            className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400"
                                                            initial={{ x: '100%' }}
                                                            whileHover={{ x: 0 }}
                                                            transition={{ duration: 0.3 }}
                                                        />
                                                    </motion.a>
                                                    <motion.a
                                                        href="#contact"
                                                        whileHover={{ scale: 1.05, y: -2 }}
                                                        whileTap={{ scale: 0.95 }}
                                                        className="btn-secondary shadow-lg hover:shadow-xl font-semibold backdrop-blur-sm"
                                                    >
                                                        Get In Touch
                                                    </motion.a>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Scroll Indicator */}
                <AnimatePresence>
                    {showSubtext && (
                        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
                            <ScrollIndicator targetId="#profile" />
                        </div>
                    )}
                </AnimatePresence>
            </div>

            {/* Bottom Gradient Fade */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white via-white/80 to-transparent z-20" />
        </Section>
    );
};

export default Hero;